import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { EditExhaustTitleDialogComponent } from '@components/exhaust-setting/edit-exhaust-title-dialog/edit-exhaust-title-dialog.component';
import { ExhaustGasSettingService } from '@service/exhaust-gas-setting.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAllTypesSet } from 'icreate/common/models/channel-type';
import { ExhaustGasSetting, maxExhaustSettings } from 'icreate/common/models/exhaust-gas-setting';
import { Vessel } from 'icreate/common/models/vessel';
import _ from 'lodash';
import { fromEvent, lastValueFrom, Subject, Subscription, take, takeUntil } from 'rxjs';

@Component({
    selector: 'app-exhaust-gas-setting',
    templateUrl: './exhaust-gas-setting.component.html',
    styleUrl: './exhaust-gas-setting.component.scss',
    standalone: false,
})
export class ExhaustGasSettingComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() vessel!: Vessel;
    @Output() pageUpdated = new EventEmitter<boolean>();
    @Input() userCanEdit: boolean = true;
    @ViewChild('otherOptions') otherOptions: ElementRef;
    @ViewChild('removeAvgConfirmation') removeAvgConfirmation: ConfirmationWindowComponent;

    initialSettings: ExhaustGasSetting[];
    currentSettings: ExhaustGasSetting[];

    allowedTypesSet: Set<ChannelType> = getAllTypesSet();

    isLoading: boolean = false;
    isAvgInfoMessageVisible: ExhaustGasSetting = null;
    optionsSettingOpen: ExhaustGasSetting;

    settingsUpdatedSubscription: Subscription;
    private destroy$: Subject<void> = new Subject();

    constructor(
        private exhaustGasSettingService: ExhaustGasSettingService,
        private dialog: MatDialog,
        private messageDialogService: MessageDialogService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.otherOptions?.nativeElement.contains(event.target) && this.optionsSettingOpen) {
            this.optionsSettingOpen = null;
        }
    }

    async ngOnInit() {
        try {
            this.isLoading = true;
            if (!this.vessel) return;
            let exhaustSettings = await this.exhaustGasSettingService.getSettings(this.vessel.id);

            if (!exhaustSettings || exhaustSettings.length == 0) {
                const newSetting = new ExhaustGasSetting(0, this.vessel.id);
                exhaustSettings = [newSetting];
            }
            this.currentSettings = exhaustSettings;
            this.initialSettings = _.cloneDeep(exhaustSettings);
        } catch (err) {
            console.log(err);
        } finally {
            this.isLoading = false;
        }

        const scrollContainer = document.querySelector('.channel-wrap');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.optionsSettingOpen = null;
                });
        }
    }

    ngAfterViewInit(): void {
        this.settingsUpdatedSubscription = this.exhaustGasSettingService
            .settingsUpdated$()
            .subscribe(({ settings, vesselId }) => {
                if (vesselId === this.vessel.id) {
                    this.currentSettings = settings;
                    this.initialSettings = _.cloneDeep(settings);
                }
            });
    }

    ngOnDestroy(): void {
        this.settingsUpdatedSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    getChannelName(channel: Channel) {
        if (!channel) return;
        return channel.opsData.name;
    }

    async selectAvgChannel(setting: ExhaustGasSetting) {
        if (!this.userCanEdit) return;
        const assignedChannel = setting.averageChannelData ? setting.averageChannelData : null;

        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vessel?.id,
                assignedChannel: assignedChannel,
                allowedTypesSet: new Set([ChannelType.EXH_GAS_MEAN]),
                setShouldDisableTypes: true,
            },
        });

        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (!channelSelected) return;
        setting.averageChannel = channelSelected.id;
        setting.averageChannelData = channelSelected;
        this.onExhaustUpdated();
    }

    async removeAvgChannel(setting: ExhaustGasSetting) {
        if (!this.userCanEdit) return;
        this.removeAvgConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.removeAvgConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption == ConfirmationWindowTypes.SAVE) {
            setting.averageChannel = null;
            setting.averageChannelData = null;
            this.onExhaustUpdated();
        }
    }

    openOptions(setting: ExhaustGasSetting) {
        this.optionsSettingOpen = setting;
    }

    async editSetting(setting: ExhaustGasSetting) {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(EditExhaustTitleDialogComponent, {
            width: '474px',
            data: { currentSetting: setting, currentSettings: this.currentSettings },
        });

        const newTitle = await lastValueFrom(dialogRef.afterClosed());
        if (newTitle) {
            setting.title = newTitle;
            this.onExhaustUpdated();
        }

        this.optionsSettingOpen = null;
    }

    async duplicateSetting(setting: ExhaustGasSetting) {
        if (this.isAddDisabled()) return;

        const lastIndex = this.currentSettings.length;
        const newSetting = new ExhaustGasSetting(lastIndex, this.vessel.id);
        newSetting.averageChannel = setting.averageChannel;
        newSetting.averageChannelData = setting.averageChannelData;

        const dialogRef = this.dialog.open(EditExhaustTitleDialogComponent, {
            width: '474px',
            data: {
                currentSetting: newSetting,
                currentSettings: this.currentSettings,
                isDuplicate: true,
            },
        });
        const newTitle = await lastValueFrom(dialogRef.afterClosed());
        if (newTitle) {
            newSetting.title = newTitle;
            newSetting.settingChannels = setting.settingChannels.map(setting => {
                setting.id = null;
                setting.exhaustGasSettingId = null;
                return setting;
            });
            this.currentSettings.push(newSetting);
            this.onExhaustUpdated();
        }

        this.optionsSettingOpen = null;
    }

    deleteSetting(setting: ExhaustGasSetting) {
        if (!this.userCanEdit) return;
        const settingIndex = this.currentSettings.indexOf(setting);

        if (settingIndex || settingIndex == 0) {
            this.currentSettings.splice(settingIndex, 1);
        }
        this.onExhaustUpdated();
        this.optionsSettingOpen = null;
    }

    async save() {
        if (!this.userCanEdit) return;
        try {
            this.isLoading = true;
            await this.exhaustGasSettingService.upsert(this.currentSettings, this.vessel.id);
            this.initialSettings = _.cloneDeep(this.currentSettings);
            this.isLoading = false;
            this.showMessageDialog('exhaust-settings.updated.message');
        } catch (err) {
            console.error(err);
            this.isLoading = false;
        }
    }

    isAddDisabled(): boolean {
        return !this.currentSettings || !this.userCanEdit || this.currentSettings.length >= maxExhaustSettings;
    }

    addSetting() {
        if (this.isAddDisabled()) return;
        const lastIndex = this.currentSettings.length;
        const newSetting = new ExhaustGasSetting(lastIndex, this.vessel.id);
        this.currentSettings.push(newSetting);
        this.onExhaustUpdated();
    }

    showMessageDialog(message) {
        this.messageDialogService.openMessage(message);
    }

    onExhaustUpdated() {
        if (!this.userCanEdit) {
            this.pageUpdated.emit(false);
            return;
        }
        !_.isEqual(this.currentSettings, this.initialSettings)
            ? this.pageUpdated.emit(true)
            : this.pageUpdated.emit(false);
    }
}
